// Servicios Permanentes (bloque 7, C; §17 ítems 99 y 101): los 9 ítems SP del
// Anexo 1 (docs/normativa/anexo1_lista_verificacion_sst.md, líneas 695 a 729) y
// las preguntas opcionales del Perfil SSO que deciden si aplican (M17).
// Condiciones: el texto del Anexo 1 y, con autorización de JC (23/10/2026), el
// AM 196 Anexo 3 (docs/normativa/anexo3_seguridad_higiene.md), que es la norma que
// el Anexo 1 cita en SP 3 a SP 9. Sin respuesta, el ítem queda "Sin calcular" y no
// cuenta en el puntaje.
//
// Estado MANUAL por ahora (Pendiente / En proceso / Cumple / No cumple), con
// evidencia opcional en Documentos SSO. Cuando Kevin responda K5 (inspecciones con
// foto), estos ítems pasarán a cumplirse con una inspección (§17 ítem 119).

/** Columnas boolean NULL de company_sso_profile (M17). */
export type CampoServicios =
  | 'centro_a_mas_de_2km'
  | 'tiene_servicio_cocina'
  | 'trabajo_con_sustancias_o_suciedad'
  | 'trabajadores_cambian_ropa'
  | 'tiene_campamentos';

export type RespuestasServicios = Partial<Record<CampoServicios, boolean | null>>;

export interface PreguntaServicios {
  campo: CampoServicios;
  pregunta: string;
  cita: string;
  /** Ítems SP que decide la respuesta. */
  items: string[];
  nota?: string;
}

const A1 = 'Anexo 1 (Ministerio del Trabajo'; // fuente interna: docs/normativa/anexo1_lista_verificacion_sst.md
const A3 = 'AM 196 (2024) Anexo 3 (Seguridad e higiene'; // fuente interna: docs/normativa/anexo3_seguridad_higiene.md

export const PREGUNTAS_SERVICIOS: PreguntaServicios[] = [
  {
    campo: 'centro_a_mas_de_2km',
    pregunta: '¿El lugar de trabajo está situado a más de dos kilómetros de la población más cercana?',
    cita: `${A1}, SP 2); ${A3}, Art. 38 num. 1)`,
    items: ['SP-02'],
    nota: 'Solo cuenta con cincuenta o más trabajadores (hasta F1, con el total de la empresa).',
  },
  {
    campo: 'tiene_servicio_cocina',
    pregunta: '¿Existe servicio de cocina en el lugar de trabajo?',
    cita: `${A1}, SP 3: "En caso de existir servicios de cocina"); ${A3}, Art. 38 num. 2)`,
    items: ['SP-03'],
  },
  {
    campo: 'trabajo_con_sustancias_o_suciedad',
    pregunta: '¿El trabajo implica contacto con sustancias químicas, tóxicas, corrosivas o peligrosas, provoca una acumulación significativa de suciedad en el cuerpo o conlleva un riesgo de contaminación?',
    cita: `${A3}, Art. 40 num. 2; Art. 42 num. 1)`,
    items: ['SP-06'],
  },
  {
    campo: 'trabajadores_cambian_ropa',
    pregunta: '¿Los trabajadores deben cambiarse de ropa antes, durante o después de su jornada de trabajo?',
    cita: `${A3}, Art. 41 num. 1)`,
    items: ['SP-08'],
  },
  {
    campo: 'tiene_campamentos',
    pregunta: '¿La empresa tiene campamentos permanentes o temporales para sus trabajadores?',
    cita: `${A1}, SP 9); ${A3}, Art. 36)`,
    items: ['SP-09'],
    nota: 'El texto no dice "en caso de existir": condición por validar.',
  },
];

export interface DefinicionSP {
  id: string;
  numero_legal: number;
  titulo: string;
  /** Pregunta del Anexo 1 (sin cambiar ninguna palabra; solo se separaron las palabras pegadas del .md). */
  descripcion: string;
  normativa: string[];
  /** Líneas del Anexo 1 (.md). */
  lineas: string;
  /** Condición de aplicación con su cita; sin condición, aplica siempre. */
  condicion?: string;
  campo?: CampoServicios;
  /** Razón del N/A cuando la respuesta es "No". */
  razonNo?: string;
}

export const ITEMS_SP: DefinicionSP[] = [
  {
    id: 'SP-01', numero_legal: 1, lineas: '696-698',
    titulo: 'Botiquín de emergencia',
    descripcion: '¿Cuenta con botiquín de emergencia para primeros auxilios?',
    normativa: ['Código de Trabajo (2005) Art. 430'],
  },
  {
    id: 'SP-02', numero_legal: 2, lineas: '699-704',
    titulo: 'Comedor',
    descripcion: '¿El comedor cuenta con una adecuada salubridad y ambientación?',
    normativa: ['Código de Trabajo (2005) Art. 42', 'AM 196 (2024) Anexo 3 Art. 38'],
    condicion: 'Aplica para centros de trabajo con cincuenta o más trabajadores y situados a más de dos kilómetros de la población más cercana (Anexo 1, SP 2; AM 196 Anexo 3 Art. 38 num. 1).',
    campo: 'centro_a_mas_de_2km',
    razonNo: 'El lugar de trabajo no está a más de dos kilómetros de la población más cercana (Anexo 1, SP 2; AM 196 Anexo 3 Art. 38 num. 1)',
  },
  {
    id: 'SP-03', numero_legal: 3, lineas: '705-707',
    titulo: 'Servicio de cocina',
    descripcion: '¿En caso de existir servicios de cocina, se cuenta con una adecuada salubridad y almacenamiento de productos alimenticios?',
    normativa: ['AM 196 (2024) Anexo 3'],
    condicion: 'En caso de existir servicios de cocina (Anexo 1, SP 3; AM 196 Anexo 3 Art. 38 num. 2).',
    campo: 'tiene_servicio_cocina',
    razonNo: 'La empresa no tiene servicio de cocina (Anexo 1, SP 3: "En caso de existir servicios de cocina")',
  },
  {
    id: 'SP-04', numero_legal: 4, lineas: '708-710',
    titulo: 'Agua para el consumo humano',
    descripcion: '¿En el lugar y/o centro de trabajo se dispone de abastecimiento de agua para el consumo humano?',
    normativa: ['AM 196 (2024) Anexo 3'],
  },
  {
    id: 'SP-05', numero_legal: 5, lineas: '711-713',
    titulo: 'Servicios higiénicos',
    descripcion: '¿Cuenta con servicios higiénicos, excusados y urinarios en buenas condiciones con separación para hombres y mujeres?',
    normativa: ['AM 196 (2024) Anexo 3'],
  },
  {
    id: 'SP-06', numero_legal: 6, lineas: '714-715',
    titulo: 'Duchas',
    descripcion: '¿Cuenta con duchas en buenas condiciones?',
    normativa: ['AM 196 (2024) Anexo 3 Arts. 40 y 42'],
    condicion: 'Cuando la naturaleza del trabajo implique contacto con sustancias químicas, tóxicas, corrosivas o peligrosas o provoque una acumulación significativa de suciedad en el cuerpo (AM 196 Anexo 3 Art. 40 num. 2), o las actividades conlleven un riesgo de contaminación (Art. 42 num. 1).',
    campo: 'trabajo_con_sustancias_o_suciedad',
    razonNo: 'El trabajo no implica contacto con sustancias peligrosas, acumulación de suciedad ni riesgo de contaminación (AM 196 Anexo 3 Arts. 40 num. 2 y 42 num. 1)',
  },
  {
    id: 'SP-07', numero_legal: 7, lineas: '716-718',
    titulo: 'Lavabos y útiles de aseo',
    descripcion: '¿Cuenta con lavabos en buenas condiciones y con útiles de aseo personal?',
    normativa: ['AM 196 (2024) Anexo 3'],
  },
  {
    id: 'SP-08', numero_legal: 8, lineas: '719-721',
    titulo: 'Vestuarios',
    // El Anexo 1 dice "separos por sexo" (l. 719; así está en el PDF oficial firmado, no es
    // error del OCR): en pantalla se muestra "separados" (decisión de JC, 24/10/2026; §17 ítem 120).
    descripcion: '¿Se dispone de vestuarios, separados por sexo, limpios y en buenas condiciones?',
    normativa: ['AM 196 (2024) Anexo 3 Art. 41'],
    condicion: 'Si los trabajadores deben cambiarse de ropa antes, durante o después de su jornada de trabajo (AM 196 Anexo 3 Art. 41 num. 1).',
    campo: 'trabajadores_cambian_ropa',
    razonNo: 'Los trabajadores no deben cambiarse de ropa en el trabajo (AM 196 Anexo 3 Art. 41 num. 1)',
  },
  {
    id: 'SP-09', numero_legal: 9, lineas: '722-729',
    titulo: 'Campamentos',
    descripcion: '¿Cuenta campamentos en buenas condiciones? Luz eléctrica; ventilación; agua para el consumo humano; servicios higiénicos (excusado, lavabo, duchas); comedores; alojamiento y vestuarios separados para hombres y mujeres.',
    normativa: ['AM 196 (2024) Anexo 3 Art. 36'],
    condicion: 'Si la empresa tiene campamentos permanentes o temporales (AM 196 Anexo 3 Art. 36). El Anexo 1 no dice "en caso de existir": condición por validar.',
    campo: 'tiene_campamentos',
    razonNo: 'La empresa no tiene campamentos (AM 196 Anexo 3 Art. 36)',
  },
];

export const PESO_SP = 3;
export const MIN_TRABAJADORES_COMEDOR = 50;

export function preguntaDe(campo: CampoServicios): PreguntaServicios {
  return PREGUNTAS_SERVICIOS.find(p => p.campo === campo)!;
}

export function razonSinResponder(campo: CampoServicios): string {
  return `Sin calcular: responde en el Perfil SSO "${preguntaDe(campo).pregunta}"`;
}

export interface AplicaSP {
  aplica: boolean;
  sin_calcular?: boolean;
  razon_no_aplica?: string;
}

/**
 * "Aplica" de un ítem SP. `trabajadores` es el total de la empresa (hasta F1).
 * El perfil incompleto (sin trabajadores) lo resuelve el motor para SP-02
 * (ITEMS_DEPENDEN_DEL_PERFIL), igual que en los demás ítems.
 */
export function aplicaSP(def: DefinicionSP, respuestas: RespuestasServicios, trabajadores: number): AplicaSP {
  if (def.id === 'SP-02' && trabajadores < MIN_TRABAJADORES_COMEDOR) {
    return { aplica: false, razon_no_aplica: 'Menos de cincuenta trabajadores: el comedor se exige desde cincuenta (Anexo 1, SP 2; AM 196 Anexo 3 Art. 38 num. 1)' };
  }
  if (!def.campo) return { aplica: true };
  const r = respuestas[def.campo];
  if (r == null) return { aplica: false, sin_calcular: true, razon_no_aplica: razonSinResponder(def.campo) };
  return r ? { aplica: true } : { aplica: false, razon_no_aplica: def.razonNo };
}

/**
 * Preguntas del Perfil SSO sin responder que hoy dejan algún ítem SP "Sin calcular"
 * (bloque 7, revisión de JC del 24/10/2026). No cuenta una pregunta que no decide nada
 * (p. ej. la distancia con menos de cincuenta trabajadores) ni el perfil incompleto,
 * que tiene su propio aviso.
 */
export function preguntasSinResponder(items: { id: string; sin_calcular?: boolean; razon_no_aplica?: string }[]): PreguntaServicios[] {
  return PREGUNTAS_SERVICIOS.filter(q => items.some(i =>
    q.items.includes(i.id) && i.sin_calcular && i.razon_no_aplica === razonSinResponder(q.campo)));
}

export function textoPreguntasSinResponder(n: number): string {
  return n === 1
    ? 'Tienes 1 pregunta del Perfil SSO sin responder: hay ítems que no se pueden calcular todavía'
    : `Tienes ${n} preguntas del Perfil SSO sin responder: hay ítems que no se pueden calcular todavía`;
}
